import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { ReactiveFormsModule } from '@angular/forms';
import { ContainerTypeService } from '../../core/services/container-type.service';
import { MaintenanceTypeService } from '../../core/services/maintenance-type.service';
import { MaintenanceService } from '../../core/services/maintenance.service';
import { RoleService } from '../../core/services/role.service';
import { TaskService } from '../../core/services/task.service';
import { AcceptanceCriteriaService } from '../../core/services/acceptance-criteria.service';
import { StageCreateComponent } from './stage-create/stage-create.component';
import { HttpErrorResponse } from '@angular/common/http';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-maintenance-create',
  templateUrl: './maintenance-create.component.html',
  styleUrls: ['./maintenance-create.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatProgressBarModule,
    StageCreateComponent,
    MatCheckboxModule,
  ],
})
export class MaintenanceCreateComponent implements OnInit {
  firstFormGroup!: FormGroup;
  secondFormGroup!: FormGroup;
  containerTypes: any[] = [];
  maintenanceTypes: any[] = [];
  roles: any[] = [];
  statuses: any[] = [
    { value: true, viewValue: 'Activo' },
    { value: false, viewValue: 'Inactivo' },
  ];
  errorMessage: string | null = null;

  tasks: any[] = [];
  accordionStages: FormArray = this.fb.array([]);
  acceptanceCriterias: any[] = [];
  showProgressBar: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private containerTypeService: ContainerTypeService,
    private maintenanceTypeService: MaintenanceTypeService,
    private maintenanceService: MaintenanceService,
    private roleService: RoleService,
    private taskService: TaskService,
    private acceptanceCriteriaService: AcceptanceCriteriaService,
  ) {}

  ngOnInit() {
    this.firstFormGroup = this.fb.group({
      containerType: ['', Validators.required],
      maintenanceType: ['', Validators.required],
      role: ['', Validators.required],
      rolApprover: ['', Validators.required],
      status: ['', Validators.required],
    });

    this.secondFormGroup = this.fb.group({
      stages: this.accordionStages,
    });

    this.loadContainerTypes();
    this.loadMaintenanceTypes();
    this.loadRoles();
    this.loadTasks();
    this.loadAcceptanceCriterias();
  }

  loadContainerTypes() {
    this.containerTypeService.getContainerTypes().subscribe(data => {
      this.containerTypes = data;
    });
  }

  loadMaintenanceTypes() {
    this.maintenanceTypeService.getMaintenanceTypes().subscribe(data => {
      this.maintenanceTypes = data;
    });
  }

  loadRoles() {
    this.roleService.getRoles().subscribe(data => {
      this.roles = data;
    });
  }

  loadTasks() {
    this.taskService.getTasks().subscribe(data => {
      this.tasks = data;
    });
  }

  loadAcceptanceCriterias() {
    this.acceptanceCriteriaService.getAcceptanceCriteria().subscribe(data => {
      this.acceptanceCriterias = data;
    });
  }

  addStageAccordion() {
    const stageGroup = this.fb.group({
      stageName: ['', Validators.required],
      tasks: this.fb.array([]),
      order: [null, Validators.required],
      required: [false],
    });
    this.accordionStages.push(stageGroup);
  }

  removeStage(index: number) {
    this.accordionStages.removeAt(index);
  }

  getStageFormArray(): FormArray {
    return this.secondFormGroup.get('stages') as FormArray;
  }

  onSubmit() {
    console.log(this.secondFormGroup.value);
    this.showProgressBar = true;
    // Verificación de que exista al menos un stage
    if (this.accordionStages.length === 0) {
      this.errorMessage = 'Debe agregar al menos una etapa.';
      this.showProgressBar = false;
      return;
    }
    // Marcar todos los campos como tocados para mostrar los mensajes de error
    this.markAllFieldsAsTouched();

    if (
      this.firstFormGroup.valid &&
      this.secondFormGroup.valid &&
      this.accordionStages.length > 0
    ) {
      const formData = {
        ...this.firstFormGroup.value,
        stages: this.accordionStages.value,
      };
      this.maintenanceService.createMaintenanceFull(formData).subscribe(
        () => {
          this.showProgressBar = false;
          this.router.navigate(['/maintenances']);
        },
        (error: HttpErrorResponse) => {
          // Verifica si el cuerpo de la respuesta contiene el mensaje de error
          if (error.status === 409 && error.error) {
            this.errorMessage = error.error;
          } else if (error.message) {
            this.errorMessage = error.message;
          } else {
            this.errorMessage = `Error desconocido (${error.status}): ${error.statusText}`;
          }
          this.showProgressBar = false;
        },
      );
    } else {
      console.log('no paso validaciones');
      // Imprime errores de validación del primer formulario
      Object.keys(this.firstFormGroup.controls).forEach(field => {
        const control = this.firstFormGroup.get(field);
        if (control?.invalid) {
          console.log(`Error en el campo ${field}:`, control.errors);
        }
      });

      // Imprime errores de validación del segundo formulario (stages)
      Object.keys(this.secondFormGroup.controls).forEach(field => {
        const control = this.secondFormGroup.get(field);
        if (control?.invalid) {
          console.log(`Error en el campo ${field}:`, control.errors);
        }
      });

      // Verificar errores en cada etapa del accordionStages
      this.accordionStages.controls.forEach((stageGroup, index) => {
        const stageFormGroup = stageGroup as FormGroup;
        Object.keys(stageFormGroup.controls).forEach(field => {
          const control = stageFormGroup.get(field);
          if (control?.invalid) {
            console.log(`Error en el campo Stage ${index} - ${field}:`, control.errors);
          }
        });
      });
      this.markAllFieldsAsTouched();
      this.showProgressBar = false;
    }
  }

  markAllFieldsAsTouched() {
    // Marcar todos los controles del primer formulario como tocados y sucios
    Object.keys(this.firstFormGroup.controls).forEach(field => {
      const control = this.firstFormGroup.get(field);
      control?.markAsTouched();
      control?.markAsDirty();
    });

    // Recorrer cada etapa (stage) en el formulario
    this.accordionStages.controls.forEach(stageGroup => {
      const stageFormGroup = stageGroup as FormGroup;

      // Marcar todos los controles dentro de la etapa como tocados y sucios
      if (stageFormGroup) {
        Object.keys(stageFormGroup.controls).forEach(field => {
          const control = stageFormGroup.get(field);
          control?.markAsTouched();
          control?.markAsDirty();
        });

        // Obtener el FormArray de tareas dentro de la etapa
        const tasksFormArray = stageFormGroup.get('tasks') as FormArray;
        if (tasksFormArray) {
          tasksFormArray.controls.forEach(taskGroup => {
            const taskFormGroup = taskGroup as FormGroup;

            // Marcar todos los controles dentro de la tarea como tocados y sucios
            if (taskFormGroup) {
              Object.keys(taskFormGroup.controls).forEach(field => {
                const control = taskFormGroup.get(field);
                control?.markAsTouched();
                control?.markAsDirty();
              });

              // Marcar todos los criterios de aceptación dentro de la tarea
              const acceptanceCriterias = taskFormGroup.get('acceptanceCriterias') as FormArray;
              if (acceptanceCriterias) {
                acceptanceCriterias.controls.forEach(criteriaGroup => {
                  const criteriaFormGroup = criteriaGroup as FormGroup;
                  if (criteriaFormGroup) {
                    Object.keys(criteriaFormGroup.controls).forEach(field => {
                      const control = criteriaFormGroup.get(field);
                      control?.markAsTouched();
                      control?.markAsDirty();
                    });

                    // Marcar todos los elementos dentro de los criterios de aceptación
                    const criteriaElements = criteriaFormGroup.get('elements') as FormArray;
                    if (criteriaElements) {
                      criteriaElements.controls.forEach(elementGroup => {
                        const elementFormGroup = elementGroup as FormGroup;
                        if (elementFormGroup) {
                          Object.keys(elementFormGroup.controls).forEach(field => {
                            const control = elementFormGroup.get(field);
                            control?.markAsTouched();
                            control?.markAsDirty();
                          });

                          // Marcar todas las opciones dentro de los elementos
                          const elementOptions = elementFormGroup.get(
                            'elementOptions',
                          ) as FormArray;
                          if (elementOptions) {
                            elementOptions.controls.forEach(optionGroup => {
                              const optionFormGroup = optionGroup as FormGroup;
                              if (optionFormGroup) {
                                Object.keys(optionFormGroup.controls).forEach(field => {
                                  const control = optionFormGroup.get(field);
                                  control?.markAsTouched();
                                  control?.markAsDirty();
                                });
                              }
                            });
                          }
                        }
                      });
                    }
                  }
                });
              }

              // Marcar todos los elementos dentro de la tarea
              const elementsFormArray = taskFormGroup.get('elements') as FormArray;
              if (elementsFormArray) {
                elementsFormArray.controls.forEach(elementGroup => {
                  const elementFormGroup = elementGroup as FormGroup;
                  if (elementFormGroup) {
                    Object.keys(elementFormGroup.controls).forEach(field => {
                      const control = elementFormGroup.get(field);
                      control?.markAsTouched();
                      control?.markAsDirty();
                    });

                    // Marcar todas las opciones dentro de los elementos
                    const elementOptions = elementFormGroup.get('elementOptions') as FormArray;
                    if (elementOptions) {
                      elementOptions.controls.forEach(optionGroup => {
                        const optionFormGroup = optionGroup as FormGroup;
                        if (optionFormGroup) {
                          Object.keys(optionFormGroup.controls).forEach(field => {
                            const control = optionFormGroup.get(field);
                            control?.markAsTouched();
                            control?.markAsDirty();
                          });
                        }
                      });
                    }
                  }
                });
              }
            }
          });
        }
      }
    });
  }

  getFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }
}
